import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  can as canWith,
  permissionsInCompany,
  reachesRecord,
  type Me,
  type PermissionKey,
  type RecordOwnership,
  type Scope,
} from '@ismtur/core';
import { api, ApiFailure, whenUnauthorized } from '../lib/api';
import { forgetPush } from '../lib/push';

/**
 * Who is signed in and what they may do in the active agency. The console uses
 * the permissions only to decide what to show; the API checks every request on
 * its own, so a hidden button is a courtesy, not the protection.
 */
export const useSession = defineStore('session', () => {
  const me = ref<Me | null>(null);
  const loaded = ref(false);

  const user = computed(() => me.value?.user ?? null);
  const active = computed(() => me.value?.active ?? null);
  const tenants = computed(() => me.value?.tenants ?? []);

  function can(key: PermissionKey, scope?: Scope): boolean {
    return active.value ? canWith(active.value.permissions, key, scope) : false;
  }

  /** Whether `key` holds at all in one company, with roles per company (ADR 0017). */
  function canIn(key: PermissionKey, companyId: string | null): boolean {
    return active.value ? permissionsInCompany(active.value, companyId)[key] !== undefined : false;
  }

  /** Whether a scoped permission reaches one record, as the API decides it. */
  function reaches(key: PermissionKey, record: RecordOwnership): boolean {
    const a = active.value;
    if (!a || !user.value) return false;
    return reachesRecord(
      a,
      key,
      { userId: user.value.id, departmentIds: a.departmentIds, companyIds: a.companyIds },
      record,
    );
  }

  async function load(): Promise<void> {
    try {
      me.value = await api.get<Me>('/auth/me');
    } catch (e) {
      // No connection is not a sign-out: say so, and come back here when it returns.
      if (e instanceof ApiFailure && e.status === 0) {
        const here = window.location.pathname + window.location.search;
        window.location.replace(`/offline.html?next=${encodeURIComponent(here)}`);
        return;
      }
      me.value = null;
    } finally {
      loaded.value = true;
    }
  }

  async function login(email: string, password: string): Promise<void> {
    await api.post('/auth/login', { email, password });
    await load();
  }

  async function logout(): Promise<void> {
    await api.post('/auth/logout').catch(() => {});
    void forgetPush();
    me.value = null;
  }

  async function switchTenant(tenantId: string): Promise<void> {
    me.value = await api.post<Me>('/auth/switch-tenant', { tenantId });
  }

  function replace(next: Me): void {
    me.value = next;
  }

  whenUnauthorized(() => {
    me.value = null;
  });

  return {
    me,
    loaded,
    user,
    active,
    tenants,
    can,
    canIn,
    reaches,
    load,
    login,
    logout,
    switchTenant,
    replace,
  };
});
